import { Fragment, useRef, useLayoutEffect, useCallback, useState, useEffect } from 'react';
import { Button, Input, Table, Space, Tree, Menu, Dropdown, Modal, Form, Select, Tag, Radio, message, Tooltip, Divider } from 'antd';
import { fullPagerConfig } from '@/utils/property';
import PermissionAdd from './PermissionAdd';
import PermissionSort from './PermissionSort';
import { get, post, put, delte } from '@/core/request';
import { getTreeObj } from '@utils/utils';
import { debounce } from 'lodash';
import './PermissionList.less';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY, MAIN_TOOLBAR_DEDUCT_SELECTORS } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
export default function PermissionList() {
    const [form] = Form.useForm();
    const [treeState, setTreeState] = useState({
        searchValue: '',
        expandedKeys: [],
        autoExpandParent: true,
        selectedKeys: [],
    });
    const [tagString, setTagString] = useState('');
    const [treeData, setTreeData] = useState([]);
    // 编辑分组信息
    const [editGroup, setEditGroup] = useState(null);
    const [moduleState, setModuleState] = useState({
        moduleList: [],
        groupList: [],
    });

    // 新增权限分组的所属业务模块修改
    const handleModuleChange = value => {
        if (value) {
            const obj = getTreeObj(moduleState.moduleList, [value]);
            setModuleState({
                ...moduleState,
                groupList: obj.groupList,
            });
            form.setFieldsValue({
                pid: undefined,
            });
        }
    };
    // 传给后端的权限分组state
    const [groupState, setGroupState] = useState({
        moduleCode: '',
        groupId: undefined,
        type: undefined,
    });

    const [tableData, setTableData] = useState({});
    const [pageSize, setPageSize] = useState(20);
    const [currPage, setCurrPage] = useState(1);

    const changePageSize = pageSize => {
        setPageSize(pageSize);
    };

    const changeCurrPage = page => {
        setCurrPage(page);
    };

    // fixed-scroll 三件套
    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: MAIN_TOOLBAR_DEDUCT_SELECTORS,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    // 渲染table数据
    const getData = () => {
        get(`/enterprise/permissions/query_page`, {
            limit: pageSize,
            page: currPage,
            moduleCode: groupState.moduleCode,
            groupId: groupState.groupId,
            type: groupState.type,
        }).then(res => {
            if (res.status_code === 200) {
                const { list } = res.data;
                list.forEach(item => {
                    item.key = item.id;
                });
                setTableData(res.data);
            }
        });
    };

    useEffect(() => {
        getData();
    }, [currPage, pageSize, groupState.moduleCode, groupState.groupId, groupState.type]);

    // 渲染 Tree数据
    const getTreeData = () => {
        let arr = [];
        const moduleArr = [];
        get(`/enterprise/permissions/get_group_modules`).then(res => {
            if (res.status_code === 200) {
                arr = res.data.map(item => {
                    moduleArr.push(item);
                    setModuleState({
                        moduleList: moduleArr,
                        groupList: [],
                    });
                    return {
                        ...item,
                        title: item.moduleName,
                        key: item.moduleId,
                        children:
                            item.groupList
                            && item.groupList.map(elem => {
                                return {
                                    ...elem,
                                    title: elem.groupName,
                                    key: elem.groupId,
                                    children:
                                        elem.secondGroupList
                                        && elem.secondGroupList.map(element => {
                                            return {
                                                ...element,
                                                title: element.groupName,
                                                key: element.groupId,
                                            };
                                        }),
                                };
                            }),
                    };
                });
                setTreeData(arr);
            }
        });
    };

    useEffect(() => {
        getTreeData();
    }, []);

    // 新增权限分组
    const formLayout = {
        labelCol: { span: 6 },
        wrapperCol: { span: 18 },
    };
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [pedding, setPedding] = useState(false);
    const [tip, setTip] = useState('');

    const handleAddPermissionGroup = (tip, row) => {
        return () => {
            setTip(tip);
            setIsModalVisible(true);
        };
    };

    const handleOk = () => {
        if (pedding) return;
        setPedding(true);
        form.validateFields()
            .then(values => {
                if (!values.moduleCode) return message.warning('所属业务模块不能为空');
                if (!values.name) return message.warning('权限分组名称不能为空');
                if (tip === 'add') {
                    values.weight = 0;
                    post(`/enterprise/groups/save-group`, values).then(res => {
                        if (res.status_code === 200) {
                            message.success(res.message);
                            getTreeData();
                        }
                    });
                } else {
                    values.id = editGroup.id;
                    put(`/enterprise/groups/update`, values).then(res => {
                        if (res.status_code === 200) {
                            message.success(res.message);
                            getTreeData();
                        }
                    });
                }
                handleCancel();
            })
            .catch(err => {});
        setPedding(false);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    // 删除权限分组
    const deleteGroupConfirm = element => {
        Modal.confirm({
            title: '提示',
            icon: <i className="bi bi-exclamation-circle"></i>,
            content: '确认是否删除该权限分组',
            okText: '确认',
            cancelText: '取消',
            onOk: () => {
                const form = new FormData();
                form.append('id', element.groupId);
                delte('/enterprise/groups/delete', form).then(res => {
                    if (res.status_code === 200) {
                        message.success(res.message);
                        getTreeData();
                        setTagString('');
                        setTreeState({
                            ...treeState,
                            selectedKeys: [],
                        });
                        setGroupState({
                            ...groupState,
                            moduleCode: undefined,
                            groupId: undefined,
                        });
                    }
                });
            },
        });
    };

    const permissionSortModal = useRef();
    // tree一级菜单点击
    const handleParentMenuClick = elem => {
        return e => {
            permissionSortModal.current.show(elem);
        };
    };

    // tree二级菜单点击
    const handleChildrenMenuClick = elem => {
        return e => {
            if (e.key === 'edit') {
                setTip('edit');
                setIsModalVisible(true);
                get(`/enterprise/groups/get`, { id: elem.groupId }).then(res => {
                    if (res.status_code === 200) {
                        setEditGroup(res.data);
                        const obj = getTreeObj(moduleState.moduleList, [res.data.modeleCode]);
                        if (obj) {
                            setModuleState({
                                ...moduleState,
                                groupList: obj.groupList,
                            });
                            form.setFieldsValue({
                                moduleCode: res.data.modeleCode,
                                pid: res.data.pid,
                                name: res.data.name,
                            });
                        }
                    }
                });
            } else {
                const bool = elem.children && elem.children.length > 0;
                if (bool || elem.allPermissions) {
                    return message.error('该分组下有子分组或权限，禁止删除！');
                }
                deleteGroupConfirm(elem);
            }
        };
    };

    const handleMouseEnter = e => {
        if (e.target.className === 'edit') {
            e.target.classList.add('mystyle');
        }
    };

    const handleMouseLeave = e => {
        e.target.classList.remove('mystyle');
    };

    const handleMouseLeaveInner = e => {
        e.currentTarget.parentNode.classList.remove('mystyle');
    };

    const getParentKey = (key, tree) => {
        let parentKey;
        for (let i = 0; i < tree.length; i++) {
            const node = tree[i];
            if (node.children) {
                if (node.children.some(item => item.key === key)) {
                    parentKey = node.key;
                } else if (getParentKey(key, node.children)) {
                    parentKey = getParentKey(key, node.children);
                }
            }
        }
        return parentKey;
    };

    const onExpand = expandedKeys => {
        setTreeState({
            ...treeState,
            expandedKeys,
            autoExpandParent: false,
        });
    };
    const handleClickSearch = debounce(e => {
        const { value } = e.target;
        const getAllNode = (expandedKeys, moduleDataList) => {
            moduleDataList.forEach(item => {
                expandedKeys.push({ key: item.key, title: item.title });
                if (item.children) {
                    expandedKeys = getAllNode(expandedKeys, item.children);
                }
            });
            return expandedKeys;
        };
        const dataList1 = getAllNode([], treeData);
        const expandedKeys = dataList1
            .map(item => {
                if (item.title.indexOf(value) > -1) {
                    return getParentKey(item.key, treeData);
                }
                return null;
            })
            .filter((item, i, self) => item && self.indexOf(item) === i);
        if (expandedKeys.length === 0) {
            message.info('没有搜索到对应的权限分组');
        }
        setTreeState({
            expandedKeys,
            searchValue: value,
            autoExpandParent: true,
        });
    }, 500);

    // 搜索数据
    const loop = data =>
        data.map(item => {
            const arrFirst = [];
            const arrSecond = [];
            const arrThird = [];
            treeData.forEach(item => {
                arrFirst.push(item.key);
                item.children?.forEach(elem => {
                    arrSecond.push(elem.key);
                    elem.children?.forEach(element => {
                        arrThird.push(element.key);
                    });
                });
            });
            let level = 0;
            let left = '';
            if (arrFirst.indexOf(item.key) !== -1) {
                level = 1;
                left = '185px';
            }
            if (arrSecond.indexOf(item.key) !== -1) {
                level = 2;
                left = '160px';
            }
            if (arrThird.indexOf(item.key) !== -1) {
                level = 3;
                left = '135px';
            }
            const index = item.title.indexOf(treeState.searchValue);
            const beforeStr = item.title.substring(0, index);
            const afterStr = item.title.slice(index + treeState.searchValue.length);
            const menu = (
                <Menu onClick={handleParentMenuClick(item)}>
                    <Menu.Item key="sort">权限排序</Menu.Item>
                </Menu>
            );
            const childrenMenu = (
                <Menu onClick={handleChildrenMenuClick(item)}>
                    <Menu.Item key="edit">编辑权限分组</Menu.Item>
                    <Menu.Item key="delete">删除权限分组</Menu.Item>
                </Menu>
            );
            const title =
                index > -1 ? (
                    <div className="edit" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                        <span>
                            {beforeStr}
                            <span className="site-tree-search-value">{treeState.searchValue}</span>
                            {afterStr}
                        </span>
                        <div className="tree-edit" onMouseLeave={handleMouseLeaveInner} style={{ left }}>
                            <Dropdown popupRender={() => (level === 1 ? menu : childrenMenu)} size="small" className="permission-more-icon" placement="bottomRight">
                                <i className="bi bi-three-dots-vertical"></i>
                            </Dropdown>
                        </div>
                    </div>
                ) : (
                    <div className="edit" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                        <span>{item.title}</span>
                        <div className="tree-edit" onMouseLeave={handleMouseLeaveInner} style={{ left }}>
                            <Dropdown popupRender={() => (level === 1 ? menu : childrenMenu)} size="small" className="permission-more-icon" placement="bottomRight">
                                <i className="bi bi-three-dots-vertical"></i>
                            </Dropdown>
                        </div>
                    </div>
                );
            if (item.children) {
                return { title, key: item.key, children: loop(item.children) };
            }

            return {
                title,
                key: item.key,
            };
            // if (level === 1 || level === 2) {
            //     return <TreeNode dataRef={item} key={item.key} title={title} />;
            // } else {
            //     return <TreeNode dataRef={item} key={item.key} title={title} selectable={false} />;
            // }
        });

    // 新增权限
    const permissionModal = useRef();
    const handleAddPermission = (tip, row) => {
        return () => {
            permissionModal.current.show(tip, row);
        };
    };

    const paginationProps = {
        ...fullPagerConfig,
        showSizeChanger: { placement: 'topRight' },
        pageSize,
        size: 'small',
        total: tableData.totalCount,
        onShowSizeChange: (current, pageSize) => changePageSize(pageSize),
        onChange: page => {
            changeCurrPage(page);
        },
        current: currPage,
    };

    // 删除权限
    const deleteConfirm = element => {
        return () => {
            Modal.confirm({
                title: '提示',
                icon: <i className="bi bi-exclamation-circle"></i>,
                content: '确认是否删除权限',
                okText: '确认',
                cancelText: '取消',
                onOk: () => {
                    const form = new FormData();
                    form.append('id', element.id);
                    delte('/enterprise/permissions/delete', form).then(res => {
                        if (res.status_code === 200) {
                            message.success(res.message);
                            getData();
                        }
                    });
                },
            });
        };
    };

    // table 数据相关
    const columns = [
        { title: '权限ID', dataIndex: 'id', width: 50 },
        { title: '权限名称', dataIndex: 'name', width: 150 },
        { title: '显示名称', dataIndex: 'displayName', width: 150 },
        { title: '权限描述', dataIndex: 'description', width: 150 },
        {
            title: '权限类型',
            dataIndex: 'type',
            width: 100,
            render: operates => <span>{operates === 1 ? '菜单权限' : operates === 2 ? '功能权限' : '数据权限'}</span>,
        },
        { title: '所属业务模块', dataIndex: 'moduleName', width: 100 },
        { title: '所属权限分组', dataIndex: 'groupName', width: 150 },
        {
            title: '前端路由',
            dataIndex: 'route',
            width: 150,
            render: route => {
                if (route === 0 || route === '0' || route == null || route === '') {
                    return '-';
                }
                return route;
            },
        },
        { title: '接口路由', dataIndex: 'apiRoute', width: 150 },
        // { title: '接口请求方法', dataIndex: 'apiMethod', },
        // {
        //     title: '显示状态', dataIndex: 'hidden',
        //     render: (operates) => (<span className={operates === 0 ? "close-status" : "open-status"}>{operates === 0 ? '显示' : '隐藏'}</span>)
        // },
        {
            title: '操作',
            dataIndex: 'operate',
            fixed: 'right',
            width: 100,
            render: (operates, row) => {
                const items = [
                    {
                        label: '删除权限',
                        key: 'delete',
                        onClick: deleteConfirm(row),
                        danger: true,
                    },
                ];

                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button size="small" type="link" onClick={handleAddPermission('edit', row)}>
                            编辑
                        </Button>
                        <Dropdown menu={{ items }} placement="bottomRight">
                            <Button type="link" size="small" className="more-operation">
                                <i className="bi bi-three-dots" aria-hidden="true" />
                            </Button>
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    // tree选择
    const onSelect = (selectedKeys, info) => {
        const arr = treeData.map(item => {
            return item.moduleId;
        });
        if (arr.includes(selectedKeys[0])) {
            setGroupState({
                ...groupState,
                moduleCode: selectedKeys[0],
                groupId: undefined,
            });
        } else {
            setGroupState({
                ...groupState,
                moduleCode: undefined,
                groupId: selectedKeys[0],
            });
        }
        const treeData1 =
            treeData
            && treeData.map(item => {
                return {
                    ...item,
                    children:
                        item.children
                        && item.children.map(elem => {
                            return {
                                ...elem,
                                moduleId: elem.groupId,
                                children:
                                    elem.children
                                    && elem.children.map(element => {
                                        return {
                                            ...element,
                                            moduleId: element.groupId,
                                        };
                                    }),
                            };
                        }),
                };
            });
        const obj = getTreeObj(treeData1, selectedKeys);
        setTreeState({
            ...treeState,
            selectedKeys,
        });
        if (obj) {
            setTagString(obj.title);
        } else {
            setTagString('');
        }
    };

    const handleRefresh = useCallback(() => {
        changeCurrPage(1);
    }, []);

    // 关闭tag
    const handleTagClick = e => {
        setTagString('');
        setTreeState({
            ...treeState,
            selectedKeys: [],
        });
        setGroupState({
            ...groupState,
            moduleCode: undefined,
            groupId: undefined,
        });
    };

    const onTypeChange = e => {
        setGroupState({
            ...groupState,
            type: e.target.value,
        });
    };

    return (
        <Fragment>
            <PageHeader title="权限管理" breadcrumb={[]} />

            <div className="page-route-body page-container list-page-container enterprise-permission-page">
                <div className="enterprise-permission-content">
                    <div className="enterprise-permission-tree-column">
                        <Space className="permission-left-top">
                            <Input placeholder="输入权限分组关键字" allowClear onChange={handleClickSearch} suffix={<i className="bi bi-search"></i>} />
                            <i className="bi bi-plus-circle"></i>
                        </Space>
                        {treeData.length > 0 && (
                            <div className="enterprise-permission-tree-panel">
                                <Tree blockNode onSelect={onSelect} onExpand={onExpand} selectedKeys={treeState.selectedKeys} expandedKeys={treeState.expandedKeys} autoExpandParent={treeState.autoExpandParent} treeData={loop(treeData)} />
                            </div>
                        )}
                    </div>
                    <div className="enterprise-permission-table-column">
                        <div className="table-tag-filter enterprise-permission-filter-panel">
                            {tagString && (
                                <Tag closable onClose={handleTagClick}>
                                    {tagString}
                                </Tag>
                            )}
                            <Radio.Group onChange={onTypeChange} defaultValue={undefined} size="small">
                                <Radio.Button value={undefined}>全部类型</Radio.Button>
                                <Radio.Button value={1}>菜单权限</Radio.Button>
                                <Radio.Button value={2}>功能权限</Radio.Button>
                                <Radio.Button value={3}>数据权限</Radio.Button>
                            </Radio.Group>
                        </div>

                        <div ref={tableRegionRef} className="table-region-container enterprise-permission-table-region">
                            <div className="main-toolbar enterprise-permission-toolbar">
                                <div className="main-tools">
                                    <Space>
                                        <Button type="primary" onClick={handleAddPermission('add')}>
                                            新增权限
                                        </Button>
                                    </Space>
                                </div>
                                <div className="sub-tools">
                                    <Space>
                                        <Tooltip title="刷新列表">
                                            <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                                        </Tooltip>
                                        <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                            <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                                        </Tooltip>
                                    </Space>
                                </div>
                            </div>

                            <div className="list-table-wrap enterprise-permission-table-wrap">
                                <Table
                                    className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                                    rowKey="id"
                                    columns={columns}
                                    dataSource={tableData.list}
                                    loading={false}
                                    bordered
                                    sticky
                                    size="medium"
                                    scroll={{ x: 1500, ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                                    pagination={paginationProps}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* 新增权限modal */}
                <PermissionAdd ref={permissionModal} getData={getData}></PermissionAdd>

                {/* 权限排序 */}
                <PermissionSort ref={permissionSortModal} getTreeData={getTreeData}></PermissionSort>

                {/* 新增权限分组 */}
                {isModalVisible && (
                    <Modal title={tip === 'add' ? '新增权限分组' : '编辑权限分组'} open={isModalVisible} onOk={handleOk} onCancel={handleCancel}>
                        <Form form={form} {...formLayout}>
                            <Form.Item name="moduleCode" label="所属业务模块" required={true}>
                                <Select allowClear placeholder="选择所属业务模块" onChange={handleModuleChange}>
                                    {moduleState.moduleList
                                        && moduleState.moduleList.map(item => {
                                            return (
                                                <Select.Option key={item.moduleId} value={item.moduleId}>
                                                    {item.moduleName}
                                                </Select.Option>
                                            );
                                        })}
                                </Select>
                            </Form.Item>

                            <Form.Item name="pid" label="所属权限分组">
                                <Select allowClear placeholder="选择所属权限分组">
                                    {moduleState.groupList
                                        && moduleState.groupList.map(item => {
                                            return (
                                                <Select.Option key={item.groupId} value={item.groupId}>
                                                    {item.groupName}
                                                </Select.Option>
                                            );
                                        })}
                                </Select>
                            </Form.Item>

                            <Form.Item name="name" label="权限分组名称" required={true}>
                                <Input placeholder="输入权限分组名称" allowClear autoComplete="off" />
                            </Form.Item>
                        </Form>
                    </Modal>
                )}
            </div>
        </Fragment>
    );
}
